import React, { useEffect, useState } from 'react';
import { Col, Row, notification } from 'antd';
import useFetchReducer from 'src/hooks/useFetchReducer';
import { enableDisableScript } from '../../api/qaErrors.api';
import { EnableDisableStatus, ScriptState } from '../../types/ScriptState';
import ConfirmationModal from 'src/components/common/ConfirmationModal';
import ScriptActivationIcon from '../../ScriptActivationIcon';
import { QaError } from '../../types/QaErrors';
import ScriptActivationActions, { ScriptAction, SelectedAction, defaultStatusId } from './ScriptActivationActions';
import styles from './ScriptActivationModal.module.less';

export type ScriptStateEnhanced = ScriptState & {
  scriptName: string;
};

type ScriptModalProps = {
  data: ScriptStateEnhanced;
  afterClose: () => void;
  onSuccessSubmit: () => void;
  scriptDataProps: QaError | null;
};

const ScriptActivationModal: React.FC<ScriptModalProps> = ({ data, afterClose, onSuccessSubmit, scriptDataProps }) => {
  const [visible, setVisible] = useState(true);
  const [initialAction, setInitialAction] = useState<SelectedAction | null>(null);
  const [customMessage, setCustomMessage] = useState<string>('');
  const [selectedAction, setSelectedAction] = useState<SelectedAction>({
    action: data.active ? ScriptAction.ActivateScript : ScriptAction.DeactivateScript,
    enableReason: data.statusId ?? defaultStatusId,
    disableReason: data.statusId ?? defaultStatusId,
  });
  const [scriptStatus, activateDeactivate] = useFetchReducer<EnableDisableStatus>(null, []);

  const setActionValue = (value: SelectedAction) => {
    setSelectedAction(value);
    if (initialAction === null) {
      setInitialAction(value);
    }
  };

  const isSubmitDisabled = () => {
    return initialAction === null || initialAction === selectedAction;
  };

  const showNotification = (status: EnableDisableStatus) => {
    if ('successMessage' in status) {
      notification.success({
        message: 'success',
        description: `${status.successMessage}`,
      });
      onSuccessSubmit();
    } else {
      notification.error({
        message: 'Something went wrong',
        description: status.errorMessage,
      });
    }
  };

  const handleSubmit = () => {
    if (!scriptDataProps) return;
    activateDeactivate(() =>
      enableDisableScript(
        {
          appId: scriptDataProps.applicationId,
          siteCode: scriptDataProps.siteCode,
          scriptName: scriptDataProps.scriptName,
        },
        {
          appParameterId: data.appParameterId,
          // TODO: Custom message handling should be added once API is done
          // since it's not clear what should be sent when actionType is "maintenance_msg"
          // https://ql2.atlassian.net/browse/UI-3333?focusedCommentId=669654
          statusId:
            selectedAction.action === ScriptAction.ActivateScript
              ? selectedAction.enableReason
              : selectedAction.disableReason,
          actionType: selectedAction.action,
          customMsg: customMessage,
        }
      )
    );
  };

  useEffect(() => {
    if (scriptStatus.data) {
      showNotification(scriptStatus.data);
      setVisible(false);
    }
  }, [scriptStatus.data]);

  return (
    <ConfirmationModal
      title="Script Activation/Deactivation"
      visible={visible}
      confirmText="Submit"
      confirmDisabled={isSubmitDisabled()}
      confirmLoading={scriptStatus.loading}
      className={styles.modal}
      width={600}
      onClose={() => setVisible(false)}
      onConfirm={handleSubmit}
      afterClose={afterClose}
    >
      <div className={styles.description}>
        <Row>
          <Col span={5}>Script Path:</Col>
          <Col span={19}>
            <i>{data.scriptLocation}</i>
          </Col>
        </Row>
        <Row>
          <Col span={5}>Current Status:</Col>
          <Col span={19}>
            <ScriptActivationIcon value={data.active} className={styles.icon} />
            {data.currentState}
          </Col>
        </Row>
      </div>

      <div className={styles.actions}>
        I would like to...
        <ScriptActivationActions
          value={selectedAction}
          isActive={data.active}
          statusId={data.statusId ?? undefined}
          setValue={setActionValue}
          setCustomMessage={setCustomMessage}
        />
      </div>
    </ConfirmationModal>
  );
};

export default ScriptActivationModal;
